/**
 * @module ui/ui_human-urls/handlers
 * @description
 * Обработчики событий для человекочитаемых URL фильтров и сортировки
 */

import * as HumanReadableUrls from '@/ui/ui_human-urls';
import { StaticFilterWidget } from '@/ui/ui_human-urls/static-filter-widget';

const SELECTORS = {
  ORDER_INPUT: 'input[name="order"]',
  ORDER_SELECT: 'select[name="order"]',
  ORDER_CHECKED: 'input[name="order"]:checked',
  ORDER_HIDDEN: 'input[name="order"][type="hidden"]'
};

/**
 * Кеш инстансов статических фильтров для форм
 */
const staticFilterInstances = new WeakMap();

/**
 * Проверяет, включены ли человекочитаемые URL в конфигурации
 */
export function isHumanReadableEnabled() {
  if (!window.Shop || !window.Shop.config) {
    return false;
  }
  const config = window.Shop.config.get('human_readable_urls');
  return Boolean(config && config.human_readable_urls);
}

/**
 * Читает значение сортировки из формы
 */
export function readOrderFromForm(form) {
  const checkedRadio = form.querySelector(SELECTORS.ORDER_CHECKED);
  if (checkedRadio && checkedRadio.value) {
    return checkedRadio.value;
  }

  const selectOrder = form.querySelector(SELECTORS.ORDER_SELECT);
  if (selectOrder && selectOrder.value) {
    return selectOrder.value;
  }

  const hiddenOrder = form.querySelector(SELECTORS.ORDER_HIDDEN);
  if (hiddenOrder && hiddenOrder.value) {
    return hiddenOrder.value;
  }

  return null;
}

/**
 * Валидирует URL перед навигацией
 */
function isValidUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Проверка на опасные протоколы
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:'];
  const lowerUrl = url.toLowerCase().trim();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.error('[HumanReadableUrls] Invalid URL protocol:', url);
      return false;
    }
  }

  // Проверка, что URL относительный или с безопасным протоколом
  try {
    const urlObj = new URL(url, window.location.origin);
    const safeProtocols = ['http:', 'https:', ''];
    return safeProtocols.includes(urlObj.protocol) || url.startsWith('/');
  } catch (e) {
    // Если не удалось распарсить как URL, проверяем что это относительный путь
    return url.startsWith('/') || !url.includes(':');
  }
}

/**
 * Строит человекочитаемый URL и выполняет навигацию
 */
function buildAndNavigate(filterInstance, state, formNode, skipDomState = false) {
  // Для сортировки НЕ читаем форму (skipDomState = true)
  // Для фильтров читаем форму (skipDomState = false)
  if (formNode && !skipDomState) {
    HumanReadableUrls.applyDomStateToState(formNode, state);
  }

  const url = filterInstance.buildHumanReadableUrl(state);
  if (!url) {
    return;
  }

  // Валидация URL перед навигацией
  if (!isValidUrl(url)) {
    console.error('[HumanReadableUrls] Invalid URL, navigation aborted:', url);
    return;
  }

  window.location.href = url;
}

/**
 * Проверяет, является ли форма виджетом фильтров
 */
function isFilterWidgetForm(form) {
  if (!form || form.tagName !== 'FORM') return false;
  return form.classList.contains('insales-human-readable-urls');
}

/**
 * Получает или создаёт экземпляр фильтра для формы
 */
async function getOrCreateFilterInstance(form) {
  if (!form || !isFilterWidgetForm(form)) {
    return null;
  }

  if (staticFilterInstances.has(form)) {
    return staticFilterInstances.get(form);
  }

  try {
    const staticInstance = new StaticFilterWidget(form);
    const initialized = await staticInstance.init();

    if (!initialized) {
      return null;
    }

    staticFilterInstances.set(form, staticInstance);
    return staticInstance;
  } catch (error) {
    console.error('[HumanReadableUrls] Failed to create filter instance:', error);
    return null;
  }
}

/**
 * Синхронизирует состояние чекбоксов формы с URL
 */
async function syncFormWithUrl(form) {
  try {
    const filterInstance = await getOrCreateFilterInstance(form);
    if (!filterInstance) {
      return;
    }

    const state = filterInstance.getCurrentState();

    // Синхронизируем характеристики
    const charCheckboxes = form.querySelectorAll("input[name='characteristics[]']");

    charCheckboxes.forEach((checkbox) => {
      const charId = parseInt(checkbox.value, 10);
      if (isNaN(charId)) return;

      const shouldBeChecked = state.selectedCharacteristicIds.includes(charId);

      if (checkbox.checked !== shouldBeChecked) {
        // Используем прямое изменение вместо click() для избежания лишних событий
        checkbox.checked = shouldBeChecked;
        // Триггерим событие change, если нужно
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Синхронизируем опции
    const optionCheckboxes = form.querySelectorAll("input[type='checkbox'][name^='options[']");

    optionCheckboxes.forEach((checkbox) => {
      const name = checkbox.name;
      const match = name && name.match(/^options\[(\d+)\]/);
      if (!match) return;

      const optionNameId = match[1];
      const valueId = parseInt(checkbox.value, 10);
      if (isNaN(valueId)) return;

      const optionValues = state.selectedOptionValueIdsByOptionNameId[optionNameId] || [];
      const shouldBeChecked = optionValues.includes(valueId);

      if (checkbox.checked !== shouldBeChecked) {
        // Используем прямое изменение вместо click() для избежания лишних событий
        checkbox.checked = shouldBeChecked;
        // Триггерим событие change, если нужно
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  } catch (error) {
    console.error('[HumanReadableUrls] Failed to sync form with URL:', error);
  }
}

/**
 * Получает основную форму фильтра (кеширует результат)
 */
let cachedMainFilterForm = null;
function getMainFilterForm() {
  if (!cachedMainFilterForm || !document.contains(cachedMainFilterForm)) {
    cachedMainFilterForm = document.querySelector('form.insales-human-readable-urls.filter');
  }
  return cachedMainFilterForm;
}

/**
 * Получает целевую форму для обработки (основная форма фильтра или переданная форма)
 */
function getTargetFilterForm(form) {
  return getMainFilterForm() || form;
}

/**
 * Обрабатывает изменение сортировки (общая логика для всех обработчиков)
 */
async function handleSortChange(form, orderValue) {
  if (!isHumanReadableEnabled()) {
    return false;
  }

  const targetForm = getTargetFilterForm(form);

  try {
    const filterInstance = await getOrCreateFilterInstance(targetForm);
    if (!filterInstance) {
      return false;
    }

    // Получить state из URL (не читаем форму, т.к. меняем только сортировку)
    const state = filterInstance.getCurrentState();

    // Установить новую сортировку
    state.order = orderValue;

    // skipDomState = true для сортировки!
    buildAndNavigate(filterInstance, state, targetForm, true);
    return true;
  } catch (err) {
    console.error('[HumanReadableUrls] Sort change handler error:', err);
    return false;
  }
}

/**
 * Безопасный submit формы с защитой от бесконечного цикла
 */
function safeFormSubmit(form) {
  if (form.dataset.humanReadableUrlsFallback === 'true') {
    // Уже в процессе fallback submit, не делаем повторный вызов
    return;
  }

  form.dataset.humanReadableUrlsFallback = 'true';
  form.submit();
  
  // Сбрасываем флаг через небольшую задержку
  setTimeout(() => {
    delete form.dataset.humanReadableUrlsFallback;
  }, 100);
}

/**
 * Настраивает глобальные обработчики для форм фильтров и сортировки
 */
export function setupFilterWidgetHandlers() {
  if (!isHumanReadableEnabled()) {
    return;
  }

  // Синхронизируем формы с URL при загрузке (асинхронно, чтобы не блокировать рендеринг)
  const syncForms = () => {
    document.querySelectorAll('form.insales-human-readable-urls').forEach((form) => {
      syncFormWithUrl(form);
    });
  };

  // Используем requestIdleCallback для асинхронной синхронизации
  if ('requestIdleCallback' in window) {
    requestIdleCallback(syncForms, { timeout: 2000 });
  } else {
    // Fallback для браузеров без requestIdleCallback
    setTimeout(syncForms, 0);
  }

  if (!document.__insalesHumanReadableFilterWidgetSubmitBound) {
    document.addEventListener('submit', async (event) => {
      if (!isHumanReadableEnabled()) {
        return;
      }

      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      if (!isFilterWidgetForm(form)) {
        return;
      }

      event.preventDefault();

      try {
        const filterInstance = await getOrCreateFilterInstance(form);
        if (!filterInstance) {
          safeFormSubmit(form);
          return;
        }

        const state = filterInstance.getCurrentState();

        const orderFromForm = readOrderFromForm(form);
        if (orderFromForm !== null) {
          state.order = orderFromForm;
        }

        buildAndNavigate(filterInstance, state, form);
      } catch (err) {
        console.error('[HumanReadableUrls] Submit handler error:', err);
        safeFormSubmit(form);
      }
    });

    document.__insalesHumanReadableFilterWidgetSubmitBound = true;
  }

  if (!document.__insalesHumanReadableSortFormBound) {
    document.addEventListener('submit', async (event) => {
      if (!isHumanReadableEnabled()) {
        return;
      }

      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      if (!form.classList.contains('insales-human-readable-urls')) {
        return;
      }

      const hasOrderField = form.querySelector('input[name="order"], select[name="order"]');
      if (!hasOrderField) {
        return;
      }

      if (form.classList.contains('filter')) {
        return;
      }

      if (form.dataset.humanReadableUrlsProcessing === 'true') {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      try {
        const orderFromForm = readOrderFromForm(form);
        const success = await handleSortChange(form, orderFromForm);
        
        if (!success) {
          safeFormSubmit(form);
        }
      } catch (err) {
        console.error('[HumanReadableUrls] Sort submit handler error:', err);
        safeFormSubmit(form);
      }
    });

    document.addEventListener('change', async (event) => {
      if (!isHumanReadableEnabled()) {
        return;
      }

      const target = event.target;
      if (!target) return;

      if (target.name !== 'order') {
        return;
      }

      if (target.tagName !== 'SELECT' && target.type !== 'radio') {
        return;
      }

      const form = target.closest('form');
      if (!form) {
        return;
      }

      if (form.classList.contains('filter')) {
        return;
      }

      if (!form.classList.contains('insales-human-readable-urls')) {
        return;
      }

      if (form.dataset.humanReadableUrlsProcessing === 'true') {
        return;
      }

      event.preventDefault();

      form.dataset.humanReadableUrlsProcessing = 'true';

      if (target.classList.contains('js-filter-trigger') ||
          target.closest('.js-filter-trigger')) {
        event.stopPropagation();
      }

      try {
        const success = await handleSortChange(form, target.value || null);
        
        if (!success) {
          safeFormSubmit(form);
        }
      } catch (err) {
        console.error('[HumanReadableUrls] Order change handler error:', err);
        safeFormSubmit(form);
      } finally {
        setTimeout(() => {
          delete form.dataset.humanReadableUrlsProcessing;
        }, 100);
      }
    }, { capture: true }); // ← CAPTURE PHASE - срабатываем ДО темы!

    document.addEventListener('click', async (event) => {
      if (!isHumanReadableEnabled()) {
        return;
      }

      const target = event.target;
      if (!target) return;

      const orderRadio = target.closest('label')?.querySelector('input[name="order"][type="radio"]') ||
                         target.closest('li')?.querySelector('input[name="order"][type="radio"]') ||
                         target.closest('a')?.closest('form.insales-human-readable-urls')?.querySelector('input[name="order"][type="radio"]');

      if (!orderRadio) {
        return;
      }

      const form = orderRadio.closest('form.insales-human-readable-urls');
      if (!form) {
        return;
      }

      if (form.classList.contains('filter')) {
        return;
      }

      event.preventDefault();

      try {
        const success = await handleSortChange(form, orderRadio.value || null);
        
        if (!success) {
          orderRadio.checked = true;
          safeFormSubmit(form);
        }
      } catch (err) {
        console.error('[HumanReadableUrls] Radio click handler error:', err);
        orderRadio.checked = true;
        safeFormSubmit(form);
      }
    });

    document.__insalesHumanReadableSortFormBound = true;
  }
}
